<template>
  <div
    class="overflow-x-auto"
    :class="{
      'border rounded-sm': wrappingBorders,
    }"
  >
    <table class="base-table w-full table-auto">
      <!-- Table Header -->
      <template v-if="enableTHead">
        <thead class="border-b">
          <tr
            v-for="headerGroup in table.getHeaderGroups()"
            :key="headerGroup.id"
          >
            <th
              v-for="header in headerGroup.headers"
              :key="header.id"
              :colSpan="header.colSpan"
              :class="{
                'text-light font-bold': true,
                'pt-3 pb-2 uppercase text-[0.7rem]': true,
                'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300': true,
                'px-4': wrappingBorders,
                'px-2': !wrappingBorders,
                'cursor-pointer select-none': header.column.getCanSort(),
                ...(table.options.meta?.getHeaderClasses
                  ? table.options.meta?.getHeaderClasses(header)
                  : {}),
              }"
              scope="col"
              @click="(event: any) => emit('update:sort', header, event)"
            >
              <FlexRender
                v-if="!header.isPlaceholder"
                :render="header.column.columnDef.header"
                :props="header.getContext()"
              />
              {{
                {asc: '&thinsp;↑', desc: '&thinsp;↓'}[
                  header.column.getIsSorted() as string
                ]
              }}
            </th>
          </tr>
        </thead>
      </template>

      <!-- Table Body -->
      <tbody
        :class="{
          'divide-y': true,
        }"
      >
        <template v-if="table.getRowModel().rows.length">
          <tr
            v-for="row in table.getRowModel().rows"
            :key="row.id"
            :class="
              table.options.meta?.getRowClasses
                ? table.options.meta?.getRowClasses(row)
                : ''
            "
          >
            <component
              :is="cellKey === 0 ? 'th' : 'td'"
              v-for="(cell, cellKey) in row.getVisibleCells()"
              :key="cell.id"
              :scope="cellKey === 0 ? 'row' : null"
              :class="{
                'py-2 font-normal bg-primary': true,
                'px-4': wrappingBorders,
                'px-2': !wrappingBorders,
                'whitespace-nowrap': !wrapText,
                ...(table.options.meta?.getCellClasses
                  ? table.options.meta?.getCellClasses(cell)
                  : {}),
              }"
            >
              <slot
                :name="cell.column.id"
                :cell="cell"
                :row="cell.row.original"
              >
                <FlexRender
                  :render="cell.column.columnDef.cell"
                  :props="cell.getContext()"
                />
              </slot>
            </component>
          </tr>
        </template>
        <tr v-else>
          <td class="py-4 text-center" :colspan="emptyColSpan">
            <slot name="no-results">{{ emptyMessage }}</slot>
          </td>
        </tr>
      </tbody>

      <!-- Table Footer -->
      <template v-if="enableTFoot">
        <tfoot aria-hidden="true" class="border-t">
          <tr
            v-for="footerGroup in table.getFooterGroups()"
            :key="footerGroup.id"
          >
            <th
              v-for="header in footerGroup.headers"
              :key="header.id"
              :colSpan="header.colSpan"
            >
              <FlexRender
                v-if="!header.isPlaceholder"
                :render="header.column.columnDef.header"
                :props="header.getContext()"
              />
            </th>
          </tr>
        </tfoot>
      </template>
    </table>
  </div>
</template>

<script setup lang="ts">
  import {computed, toRefs} from 'vue';
  import {FlexRender, type Header} from '@tanstack/vue-table';

  const emit = defineEmits<{
    (e: 'update:sort', header: Header<any, any>, event: Event): void;
  }>();
  const props = withDefaults(
    defineProps<{
      table: any;
      enableTHead?: boolean;
      enableTFoot?: boolean;
      wrappingBorders?: boolean;
      emptyMessage?: string;
      wrapText?: boolean;
    }>(),
    {
      enableTHead: true,
      enableTFoot: false,
      wrappingBorders: true,
      emptyMessage: 'No results.',
      wrapText: false,
    }
  );

  const {table, enableTHead, enableTFoot} = toRefs(props);

  const emptyColSpan = computed(() => {
    const headerGroups = table?.value?.getHeaderGroups();

    if (headerGroups && headerGroups.length > 0) {
      const lastGroup = headerGroups[headerGroups.length - 1];
      return lastGroup.headers.length;
    }

    return 1;
  });
</script>

<style></style>
